import React, { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { MultiSelectCombobox } from "./comboBox";
import { X } from "lucide-react";

type FilterFunction = (selectedSorting: string[], data: any[]) => any[];

export interface Filter {
  [key: string]: {
    [key: string]: FilterFunction;
  };
}

interface TableFiltersProps {
  availableFilters: Filter[];
  onClearFilters?: () => void;
  data: any[];
  setData: React.Dispatch<React.SetStateAction<any[]>>;
}

const TableFilters: React.FC<TableFiltersProps> = ({
  availableFilters,
  data,
  setData,
}) => {
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [selectedValues, setSelectedValues] = useState<{
    [key: string]: string[];
  }>({});
  const [openFilters, setOpenFilters] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [filterObject, setFilterObject] = useState<Filter>({});

  const filterKeys = availableFilters.flatMap((filter) => Object.keys(filter));

  const handleSelect = (filter: string) => {
    setSelectedFilters((prev) => {
      if (prev.includes(filter)) {
        setSelectedValues((prevValues) => {
          const { [filter]: removed, ...rest } = prevValues;
          return rest;
        });

        return prev.filter((item) => item !== filter);
      } else {
        return [...prev, filter];
      }
    });
  };

  const handleValueChange = (
    filter: string,
    value: string[],
    filterObject: any
  ) => {
    setSelectedValues((prev) => ({
      ...prev,
      [filter]: value,
    }));
    setFilterObject(filterObject);
  };

  const applyAllFilters = (
    selectedValues: { [key: string]: string[] },
    filterObject: Filter,
    data: any[]
  ) => {

    let currentFilteredData = [...data];

    const applyFilterFunction = (
      filterFunction: (values: string[], data: any[]) => any[],
      values: string[]
    ) => {
      return filterFunction(values, currentFilteredData);
    }
    if (Object.keys(selectedValues).length === 0) {
      setData(currentFilteredData);
      return currentFilteredData;
    }

    for (const [filter, values] of Object.entries(selectedValues)) {
      const filterFunc = filterObject[filter];

      if (filterFunc) {
        const allValuesEmpty = Object.values(selectedValues).every(
          (v) => v.length === 0
        );

        if (allValuesEmpty) {
          const currentFilteredData = [...data];
          setData(currentFilteredData);
          return currentFilteredData;
        }

        // eslint-disable-next-line no-loop-func
        values.forEach((value) => {
          const filterFunction = filterFunc[value];

          if (filterFunction) {
            currentFilteredData = applyFilterFunction(filterFunction, values);
            setData(currentFilteredData);
            // console.log(currentFilteredData, "current filtered data");
          }
        });
      }
    }
    return currentFilteredData;
  };

  

  const toggleFilterOpen = (filter: string) => {
    setOpenFilters((prev) => {
      const newState: { [key: string]: boolean } = {};
      
      Object.keys(prev).forEach((key) => {
        newState[key] = false;
      });
      newState[filter] = !prev[filter];
      return newState;
    });
  }


  const handleDeleteFilter = (e: React.MouseEvent<SVGSVGElement>, filter: string) => {
    e.preventDefault();
    
    setSelectedValues((prevValues) => {
        const { [filter]: removed, ...rest } = prevValues; 
        return rest; 
    });

    setSelectedFilters((prev) => prev.filter((item) => item !== filter));
};

useEffect(() => {
    applyAllFilters(selectedValues, filterObject, data);
  }, [selectedValues, filterObject, data, selectedFilters]);

  return (
    <div className="flex mb-4 space-x-3 w-[150px]">
      <div className="min-w-[150px]">
        <Select onValueChange={handleSelect} value="">
          <SelectTrigger>
            <SelectValue placeholder="Select filter" />
          </SelectTrigger>
          <SelectContent>
            {filterKeys.map((key) => (
              <SelectItem key={key} value={key}>
                {key}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedFilters.map((filter) => {
        const filterObject = availableFilters.find((f) => filter in f);
        const isOpen = openFilters[filter] || false;
        return (
          <div className="flex items-center gap-[2px]" key={filter}>
            <div><X onClick={(e) => handleDeleteFilter(e, filter)} width={18}/></div>
            <MultiSelectCombobox
              displayValue={filter}
              width="w-[250px]"
              types={
                filterObject
                  ? Object.entries(filterObject[filter]).map(([key, _]) => ({
                      value: key,
                      label: key,
                    }))
                  : []
              }
              open={isOpen}
              setOpen={() => toggleFilterOpen(filter)}
              value={selectedValues[filter] || []}
              onChange={(value) => {
                handleValueChange(filter, value, filterObject);
              }}
            />
          </div>
        );
      })}
    </div>
  );
};

export default TableFilters;
